import {useEffect} from 'react'
import {Link} from '@primer/react'
import {useAddBanner, useDismissBanner, type InitialBanner} from '@github-ui/banner-stack'
import type {CachesIndexPayload} from '@github-ui/dotcom-schema/types/actions/v1/caches_index'

export const CACHES_USAGE_BANNER_ID = 'caches-usage-banner'

type CachesUsageBanner = InitialBanner & {'data-test-selector'?: string}

export type CachesUsageBannerData = Pick<CachesIndexPayload, 'usage' | 'usageHelpUrl'>

export function getCachesUsageBanner({usage, usageHelpUrl}: CachesUsageBannerData): CachesUsageBanner | undefined {
  if (!usage?.aboveWarningThreshold) return undefined

  const {cacheLimitGb, currentCacheSizeGb} = usage
  return {
    id: CACHES_USAGE_BANNER_ID,
    persistOnNavigation: true,
    title: `Approaching total cache storage limit (${currentCacheSizeGb} GB of ${cacheLimitGb} GB used)`,
    description: (
      <>
        Least recently used caches will be automatically evicted to limit the total cache storage to {cacheLimitGb} GB.{' '}
        <Link href={usageHelpUrl} inline>
          Learn more about cache usage.
        </Link>
      </>
    ),
    variant: 'warning',
    'data-test-selector': CACHES_USAGE_BANNER_ID,
  }
}

export function useCachesUsageBanner({data}: {data?: CachesUsageBannerData}) {
  const addBanner = useAddBanner()
  const dismissBanner = useDismissBanner()
  const usage = data?.usage
  const usageHelpUrl = data?.usageHelpUrl

  useEffect(() => {
    if (!usage || usageHelpUrl === undefined) return

    const banner = getCachesUsageBanner({usage, usageHelpUrl})
    if (!banner) return

    const id = addBanner(banner)

    return () => {
      dismissBanner(id)
    }
  }, [usage, usageHelpUrl, addBanner, dismissBanner])
}
